import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Lightbulb, RefreshCw, MessageSquare, TrendingDown, TrendingUp, AlertTriangle, Calendar, Mail, Download } from "lucide-react";
import { useUser } from "@/contexts/UserContext";
import apiService from "@/services/apiService";
import { teamsService } from "@/services/teamsService";
import { wellnessService } from "@/services/wellnessService";
import { useRefresh } from "@/contexts/RefreshContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/components/ui/use-toast";
import { sendOneOnOnePrepEmail } from "@/services/oneOnOnePrepEmailService";

interface OneOnOneInsight {
  teamMemberId: string;
  memberName: string;
  memberAvatar?: string;
  recentMood: "great" | "good" | "okay" | "low" | "struggling";
  keyTopics: string[];
  suggestedQuestions: string[];
  priority: "high" | "medium" | "low";
  lastCheckIn: string;
  summary: string;
  dataInsights?: string[];
  recommendedActions?: string[];
  trends?: {
    moodTrend: "improving" | "declining" | "stable";
    energyTrend: "increasing" | "decreasing" | "stable";
  };
  metrics?: {
    avgMood: string;
    avgEnergy: string;
    avgFocus: string;
    consistencyScore: string;
    daysSinceLastCheckIn: number;
  };
}

const AIOneOnOnePrepWidget = () => {
  const { user } = useUser();
  const { lastCheckInTime } = useRefresh();
  const { toast } = useToast();
  const [insights, setInsights] = useState<OneOnOneInsight[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showFullPrep, setShowFullPrep] = useState(false);
  const [selectedMember, setSelectedMember] = useState<string | null>(null);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const cacheRef = useRef<{ data: OneOnOneInsight[]; timestamp: number } | null>(null);
  const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

  const getTeamMemberData = async () => {
    if (!user?.teamId) return [];

    try {
      // Get team members via teamsService
      const membersResponse = await teamsService.getMembers(user.teamId);

      if (!membersResponse || !membersResponse.success || !membersResponse.data) {
        return [];
      }

      const teamMembers = membersResponse.data.filter((m: any) => (m.id || m.userId) !== user.id);
      const teamMemberIds = teamMembers.map((m: any) => m.id || m.userId);
      
      if (teamMemberIds.length === 0) {
        return [];
      }

      // Get team members' recent check-ins via wellnessService
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      // Get check-ins for each team member
      const checkInsPromises = teamMemberIds.map((memberId: string) => 
        wellnessService.getCheckIns({
          startDate: sevenDaysAgo.toISOString(),
          limit: 50,
          userId: memberId,
        })
      );
      
      const checkInsResponses = await Promise.all(checkInsPromises);
      const checkIns = checkInsResponses.flatMap((response: any) => 
        Array.isArray(response) ? response : (response?.data || [])
      );

      // Combine check-ins with member profile data
      return checkIns.map((checkIn: any) => {
        const member = teamMembers.find((m: any) => (m.id || m.userId) === (checkIn.userId || checkIn.user_id));
        return {
          ...checkIn,
          profiles: member ? {
            id: member.id || member.userId,
            display_name: member.name || member.displayName,
            avatar_url: member.avatarUrl || member.avatar_url,
          } : null,
        };
      });
    } catch (error) {
      console.error("Error in getTeamMemberData:", error);
      return [];
    }
  };

  const generateOneOnOneInsights = async (teamData: any[], forceRefresh = false): Promise<OneOnOneInsight[]> => {
    if (!teamData || teamData.length === 0) {
      return [
        {
          teamMemberId: "demo-1",
          memberName: "Team Member",
          recentMood: "okay",
          keyTopics: ["No recent check-ins available"],
          suggestedQuestions: ["How has your week been going?", "What support do you need?"],
          priority: "medium",
          lastCheckIn: "No recent check-ins",
          summary: "No recent data available for this team member."
        }
      ];
    }

    // Group check-ins by team member
    const memberData = teamData.reduce((acc: any, checkIn: any) => {
      const memberId = checkIn.profiles?.id || checkIn.userId || checkIn.user_id;
      if (!memberId) return acc;

      if (!acc[memberId]) {
        acc[memberId] = {
          memberName: checkIn.profiles?.display_name || checkIn.user?.name || 'Unknown',
          memberAvatar: checkIn.profiles?.avatar_url || checkIn.user?.avatarUrl,
          checkIns: []
        };
      }
      acc[memberId].checkIns.push(checkIn);
      return acc;
    }, {});

    const insights: OneOnOneInsight[] = [];

    for (const [memberId, data] of Object.entries(memberData) as [string, any][]) {
      const { memberName, memberAvatar, checkIns } = data;
      
      // Sort check-ins by date (most recent first)
      const sortedCheckIns = checkIns.sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());
      const recentCheckIn = sortedCheckIns[0];
      
      // Deep analysis of patterns and trends
      const avgMood = checkIns.reduce((sum: number, c: any) => sum + (c.mood || 0), 0) / checkIns.length;
      const avgEnergy = checkIns.reduce((sum: number, c: any) => sum + (c.energy || 0), 0) / checkIns.length;
      const avgFocus = checkIns.reduce((sum: number, c: any) => sum + (c.focus || 0), 0) / checkIns.length;
      const avgMotivation = checkIns.reduce((sum: number, c: any) => sum + (c.motivation || 0), 0) / checkIns.length;
      
      // Trend analysis (comparing first half vs second half of check-ins)
      const midPoint = Math.floor(checkIns.length / 2);
      const recentHalf = sortedCheckIns.slice(0, midPoint);
      const earlierHalf = sortedCheckIns.slice(midPoint);
      
      const recentAvgMood = recentHalf.length > 0 ? recentHalf.reduce((sum: number, c: any) => sum + (c.mood || 0), 0) / recentHalf.length : avgMood;
      const earlierAvgMood = earlierHalf.length > 0 ? earlierHalf.reduce((sum: number, c: any) => sum + (c.mood || 0), 0) / earlierHalf.length : avgMood;
      const moodTrend = recentAvgMood - earlierAvgMood;
      
      const recentAvgEnergy = recentHalf.length > 0 ? recentHalf.reduce((sum: number, c: any) => sum + (c.energy || 0), 0) / recentHalf.length : avgEnergy;
      const earlierAvgEnergy = earlierHalf.length > 0 ? earlierHalf.reduce((sum: number, c: any) => sum + (c.energy || 0), 0) / earlierHalf.length : avgEnergy;
      const energyTrend = recentAvgEnergy - earlierAvgEnergy;
      
      // Detailed analysis of content
      const allBlockers = checkIns.filter((c: any) => c.blockers && c.blockers.trim() !== "").map((c: any) => ({ text: c.blockers, date: c.date }));
      const allWins = checkIns.filter((c: any) => c.wins && c.wins.trim() !== "").map((c: any) => ({ text: c.wins, date: c.date }));
      const allReflections = checkIns.filter((c: any) => c.reflection && c.reflection.trim() !== "").map((c: any) => ({ text: c.reflection, date: c.date }));
      const helpRequests = checkIns.filter((c: any) => c.help_needed).map((c: any) => ({ helpWith: c.help_with, date: c.date }));
      
      // Task completion and productivity analysis
      const allTasks = checkIns.flatMap((c: any) => c.tasks || []);
      const avgTasksPerCheckIn = allTasks.length / checkIns.length;
      
      // Consistency analysis
      const checkInFrequency = checkIns.length;
      const consistencyScore = checkInFrequency >= 5 ? "high" : checkInFrequency >= 3 ? "medium" : "low";
      
      // Build comprehensive member analysis
      const memberAnalysis = {
        memberName,
        totalCheckIns: checkIns.length,
        dateRange: `${new Date(sortedCheckIns[sortedCheckIns.length - 1]?.date).toLocaleDateString()} to ${new Date(sortedCheckIns[0]?.date).toLocaleDateString()}`,
        
        // Current state
        currentMood: recentCheckIn?.mood || 0,
        currentEnergy: recentCheckIn?.energy || 0,
        currentFocus: recentCheckIn?.focus || 0,
        currentMotivation: recentCheckIn?.motivation || 0,
        
        // Averages and trends
        avgMood: avgMood.toFixed(1),
        avgEnergy: avgEnergy.toFixed(1),
        avgFocus: avgFocus.toFixed(1),
        avgMotivation: avgMotivation.toFixed(1),
        
        moodTrend: moodTrend > 0.3 ? "improving" : moodTrend < -0.3 ? "declining" : "stable",
        energyTrend: energyTrend > 5 ? "increasing" : energyTrend < -5 ? "decreasing" : "stable",
        
        // Content analysis
        totalBlockers: allBlockers.length,
        recentBlockers: allBlockers.slice(0, 3).map(b => b.text),
        totalWins: allWins.length,
        recentWins: allWins.slice(0, 3).map(w => w.text),
        totalReflections: allReflections.length,
        recentReflections: allReflections.slice(0, 2).map(r => r.text),
        
        // Help and support
        helpRequestCount: helpRequests.length,
        recentHelpRequests: helpRequests.slice(0, 2).map(h => h.helpWith),
        
        // Productivity indicators
        avgTasksPerCheckIn: avgTasksPerCheckIn.toFixed(1),
        consistencyScore,
        
        // Latest update
        latestReflection: recentCheckIn?.reflection || "No recent reflection",
        daysSinceLastCheckIn: Math.floor((Date.now() - new Date(recentCheckIn?.date).getTime()) / (1000 * 60 * 60 * 24))
      };

      try {
        const prompt = `As an expert management coach and people analytics specialist, analyze this team member's comprehensive check-in data to provide actionable 1:1 preparation insights. Be specific, practical, and focus on what matters most.

**TEAM MEMBER ANALYSIS:**
Name: ${memberAnalysis.memberName}
Data Period: ${memberAnalysis.dateRange} (${memberAnalysis.totalCheckIns} check-ins)
Days since last check-in: ${memberAnalysis.daysSinceLastCheckIn}

**WELLBEING & PERFORMANCE METRICS:**
Current State: Mood ${memberAnalysis.currentMood}/5, Energy ${memberAnalysis.currentEnergy}%, Focus ${memberAnalysis.currentFocus}%, Motivation ${memberAnalysis.currentMotivation}%
7-Day Averages: Mood ${memberAnalysis.avgMood}/5, Energy ${memberAnalysis.avgEnergy}%, Focus ${memberAnalysis.avgFocus}%, Motivation ${memberAnalysis.avgMotivation}%
Trends: Mood is ${memberAnalysis.moodTrend}, Energy is ${memberAnalysis.energyTrend}

**ENGAGEMENT & PRODUCTIVITY:**
Check-in Consistency: ${memberAnalysis.consistencyScore} (${memberAnalysis.totalCheckIns} check-ins in 7 days)
Average Tasks per Check-in: ${memberAnalysis.avgTasksPerCheckIn}
Reflection Engagement: ${memberAnalysis.totalReflections} meaningful reflections

**CHALLENGES & BLOCKERS:**
Total Blockers Reported: ${memberAnalysis.totalBlockers}
Recent Blockers: ${memberAnalysis.recentBlockers.length > 0 ? memberAnalysis.recentBlockers.join(" | ") : "No recent blockers"}

**WINS & ACHIEVEMENTS:**
Total Wins Shared: ${memberAnalysis.totalWins}
Recent Wins: ${memberAnalysis.recentWins.length > 0 ? memberAnalysis.recentWins.join(" | ") : "No recent wins shared"}

**SUPPORT REQUESTS:**
Help Requests: ${memberAnalysis.helpRequestCount}
Recent Help Needed: ${memberAnalysis.recentHelpRequests.length > 0 ? memberAnalysis.recentHelpRequests.join(" | ") : "No recent help requests"}

**LATEST INSIGHTS:**
Recent Reflections: ${memberAnalysis.recentReflections.length > 0 ? memberAnalysis.recentReflections.join(" | ") : "No recent reflections"}
Latest Reflection: "${memberAnalysis.latestReflection}"

**INSTRUCTIONS:**
Based on this comprehensive data, provide strategic 1:1 preparation guidance. Focus on:
1. Specific wellbeing patterns and what they indicate
2. Actionable topics based on actual data trends
3. Precise questions that address their current situation
4. Priority level based on urgency indicators
5. Concrete next steps for manager support

Return ONLY a JSON object in this exact format:
{
  "keyTopics": ["Specific topic based on data", "Another data-driven topic", "Third actionable topic"],
  "suggestedQuestions": ["Specific question about their situation", "Follow-up question", "Action-oriented question"],
  "priority": "high|medium|low",
  "summary": "Data-driven summary with specific insights and recommended focus areas",
  "dataInsights": ["Key pattern or trend observation", "Another specific insight", "Third important finding"],
  "recommendedActions": ["Specific action for manager", "Another concrete step", "Third recommendation"]
}`;

        // ALIGNED WITH web_old: Call backend endpoint with forceRefresh (lines 284-293)
        // Backend endpoint: POST /insights/one-on-one-prep?forceRefresh=false
        // MIGRATED FROM web_old/src/components/manager/AIOneOnOnePrepWidget.tsx::generateOneOnOneInsights()
        const response = await apiService.post('/insights/one-on-one-prep', {
          teamMemberId: memberId,
          teamMemberName: memberName,
          checkIns: checkIns,
          memberAnalysis,
        }, {
          params: { forceRefresh },
        });
        
        // ALIGNED WITH web_old: Backend returns format directly matching OneOnOneInsight interface
        // No parsing needed - backend format matches web_old format exactly (keyTopics, suggestedQuestions, etc.)
        const aiInsight = typeof response === 'object' && response !== null 
          ? response 
          : (typeof response === 'string' ? JSON.parse(response) : {});

        insights.push({
          teamMemberId: memberId,
          memberName,
          memberAvatar,
          recentMood: avgMood >= 4.5 ? "great" : avgMood >= 4 ? "good" : avgMood >= 3 ? "okay" : avgMood >= 2 ? "low" : "struggling",
          keyTopics: aiInsight.keyTopics || ["General check-in"],
          suggestedQuestions: aiInsight.suggestedQuestions || ["How are you feeling?"],
          priority: aiInsight.priority || "medium",
          lastCheckIn: recentCheckIn.date,
          summary: aiInsight.summary || "Regular 1:1 check-in recommended.",
          dataInsights: aiInsight.dataInsights || [],
          recommendedActions: aiInsight.recommendedActions || [],
          trends: {
            moodTrend: memberAnalysis.moodTrend as "improving" | "declining" | "stable",
            energyTrend: memberAnalysis.energyTrend as "increasing" | "decreasing" | "stable"
          },
          metrics: {
            avgMood: memberAnalysis.avgMood,
            avgEnergy: memberAnalysis.avgEnergy,
            avgFocus: memberAnalysis.avgFocus,
            consistencyScore: memberAnalysis.consistencyScore,
            daysSinceLastCheckIn: memberAnalysis.daysSinceLastCheckIn
          }
        });
      } catch (error) {
        console.error("Error generating AI insight for member:", memberName, error);
        
        // Fallback insight with basic data
        const fallbackBlockers = checkIns.filter((c: any) => c.blockers && c.blockers.trim() !== "").map((c: any) => c.blockers);
        
        insights.push({
          teamMemberId: memberId,
          memberName,
          memberAvatar,
          recentMood: avgMood >= 4.5 ? "great" : avgMood >= 4 ? "good" : avgMood >= 3 ? "okay" : avgMood >= 2 ? "low" : "struggling",
          keyTopics: fallbackBlockers.length > 0 ? [`Address blockers: ${fallbackBlockers[0]}`] : ["General well-being"],
          suggestedQuestions: [
            "How are you feeling about your current workload?",
            "What support do you need from me?",
            "What's been going well this week?"
          ],
          priority: avgMood < 3 || helpRequests.length > 0 ? "high" : "medium",
          lastCheckIn: recentCheckIn.date,
          summary: `${memberName} has ${avgMood < 3 ? "been struggling" : "been doing well"} based on recent check-ins.`,
          dataInsights: [`Average mood: ${avgMood.toFixed(1)}/5`, `${checkIns.length} check-ins in the last 7 days`],
          recommendedActions: ["Schedule a 1:1 meeting", "Review their current workload", "Offer additional support"],
          trends: {
            moodTrend: "stable" as const,
            energyTrend: "stable" as const
          },
          metrics: {
            avgMood: avgMood.toFixed(1),
            avgEnergy: avgEnergy.toFixed(1),
            avgFocus: avgFocus.toFixed(1),
            consistencyScore: checkIns.length >= 5 ? "high" : checkIns.length >= 3 ? "medium" : "low",
            daysSinceLastCheckIn: Math.floor((Date.now() - new Date(recentCheckIn?.date).getTime()) / (1000 * 60 * 60 * 24))
          }
        });
      }
    }

    return insights.slice(0, 4); // Show top 4 team members
  };

  const loadOneOnOnePrep = async (forceRefresh = false) => {
    // Check cache first
    if (!forceRefresh && cacheRef.current) {
      const cacheAge = Date.now() - cacheRef.current.timestamp;
      if (cacheAge < CACHE_DURATION) {
        setInsights(cacheRef.current.data);
        return;
      }
    }

    setIsLoading(true);
    try {
      const teamData = await getTeamMemberData();
      const oneOnOneInsights = await generateOneOnOneInsights(teamData, forceRefresh);
      
      // Update cache
      cacheRef.current = {
        data: oneOnOneInsights,
        timestamp: Date.now()
      };
      
      setInsights(oneOnOneInsights);
    } catch (error) {
      console.error("Error loading 1:1 prep:", error);
      setInsights([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Solo users should never see this widget
    if (user?.isSoloUser || user?.persona === 'independentSolo' || user?.persona === 'organizationalSolo') {
      setInsights([]);
      return;
    }
    
    if (user?.teamId) {
      // Check cache first before making any API calls
      if (cacheRef.current) {
        const cacheAge = Date.now() - cacheRef.current.timestamp;
        if (cacheAge < CACHE_DURATION) {
          setInsights(cacheRef.current.data);
          return;
        }
      }
      loadOneOnOnePrep();
    }
  }, [user?.teamId, user?.isSoloUser, user?.persona]);

  // Refresh insights when a new check-in is submitted
  useEffect(() => {
    if (lastCheckInTime && user?.teamId) {
      loadOneOnOnePrep(true); // Force refresh to bypass cache
    }
  }, [lastCheckInTime]);

  const getMoodIcon = (mood: string) => {
    switch (mood) {
      case "low":
      case "struggling":
        return <TrendingDown className="h-4 w-4 text-red-500" />;
      case "good":
      case "great":
        return <TrendingUp className="h-4 w-4 text-green-500" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-amber-500" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high":
        return "border-red-200 bg-red-50";
      case "medium":
        return "border-amber-200 bg-amber-50";
      case "low":
        return "border-green-200 bg-green-50";
      default:
        return "border-gray-200 bg-gray-50";
    }
  };

  // Format a single team member's insights for export
  const formatInsightForExport = (insight: OneOnOneInsight) => {
    return `# 1:1 Preparation for ${insight.memberName}

## Overview
- **Priority**: ${insight.priority}
- **Last Check-in**: ${insight.metrics?.daysSinceLastCheckIn} days ago
- **Consistency**: ${insight.metrics?.consistencyScore}

## Wellbeing Metrics
- **Average Mood**: ${insight.metrics?.avgMood}/5
- **Average Energy**: ${insight.metrics?.avgEnergy}%
- **Average Focus**: ${insight.metrics?.avgFocus}%

## Trends
- **Mood Trend**: ${insight.trends?.moodTrend || 'stable'}
- **Energy Trend**: ${insight.trends?.energyTrend || 'stable'}

## AI Summary
${insight.summary}

## Key Discussion Topics
${insight.keyTopics.map((topic, i) => `${i + 1}. ${topic}`).join('\n')}

## Suggested Questions
${insight.suggestedQuestions.map((q, i) => `${i + 1}. "${q}"`).join('\n')}

${insight.dataInsights && insight.dataInsights.length > 0 ? `## Data-Driven Insights
${insight.dataInsights.map(insight => `- ${insight}`).join('\n')}` : ''}

${insight.recommendedActions && insight.recommendedActions.length > 0 ? `## Recommended Actions
${insight.recommendedActions.map(action => `- ${action}`).join('\n')}` : ''}

---
`;
  };

  // Format all insights for export
  const formatAllInsightsForExport = () => {
    const header = `# 1:1 Preparation Guide
Generated on: ${new Date().toLocaleDateString()}
Team Members: ${insights.length}

---

`;
    const content = insights.map(insight => formatInsightForExport(insight)).join('\n');
    return header + content;
  };

  // Export as text file
  const handleExport = () => {
    setIsExporting(true);
    try {
      const content = formatAllInsightsForExport();
      const blob = new Blob([content], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `1-1-prep-${new Date().toISOString().split('T')[0]}.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast({
        title: "📥 Export Successful",
        description: "Your 1:1 preparation notes have been downloaded.",
      });
    } catch (error) {
      console.error("Export failed:", error);
      toast({
        title: "Export Failed",
        description: "Failed to export preparation notes. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Send email with all insights
  const handleEmailSend = async () => {
    if (!user?.email) {
      toast({
        title: "Email Error",
        description: "User email not found. Please try logging in again.",
        variant: "destructive",
      });
      return;
    }

    setIsSendingEmail(true);
    try {
      const content = formatAllInsightsForExport();
      const emailSent = await sendOneOnOnePrepEmail({
        recipientEmail: user.email,
        managerName: user.name || 'Team Leader',
        teamMemberName: 'Team Members',
        prepContent: content,
      });

      if (emailSent) {
        toast({
          title: "📧 Email Sent",
          description: `1:1 preparation guide sent to ${user.email}`,
        });
      } else {
        toast({
          title: "Email Failed",
          description: "Failed to send the preparation guide. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Failed to send email:", error);
      toast({
        title: "Email Failed",
        description: "An error occurred while sending the email.",
        variant: "destructive",
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  return (
    <>
      <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-card dark:to-muted/50 border-blue-200 dark:border-border">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-blue-100 dark:bg-muted rounded-lg">
              <Lightbulb className="h-4 w-4 text-blue-600 dark:text-primary" />
            </div>
            <div>
              <CardTitle className="text-base text-foreground">🎯 AI-Powered 1:1 Prep</CardTitle>
              <CardDescription className="text-xs">
                Personalized insights for your team conversations
              </CardDescription>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => loadOneOnOnePrep(true)}
            disabled={isLoading}
            className="h-8 w-8 p-0"
          >
            <RefreshCw className={`h-3 w-3 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="h-20 bg-white/60 animate-pulse rounded-lg"></div>
            ))}
          </div>
        ) : insights.length === 0 ? (
          <div className="text-center py-6">
            <MessageSquare className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No team check-ins available for 1:1 prep</p>
          </div>
        ) : (
          <div className="space-y-4">
            {insights.slice(0, 2).map((insight, index) => (
              <div 
                key={insight.teamMemberId} 
                className={`p-4 rounded-lg border ${getPriorityColor(insight.priority)} hover:shadow-md transition-shadow`}
              >
                {/* Header with member info and metrics */}
                <div className="flex items-start gap-3 mb-3">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={insight.memberAvatar} alt={insight.memberName} />
                    <AvatarFallback>{insight.memberName?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-sm text-foreground">{insight.memberName}</h4>
                      {getMoodIcon(insight.recentMood)}
                      <Badge 
                        variant={insight.priority === "high" ? "destructive" : insight.priority === "medium" ? "default" : "secondary"}
                        className="text-xs px-2 py-0"
                      >
                        {insight.priority}
                      </Badge>
                    </div>
                    
                    {/* Quick metrics */}
                    <div className="flex gap-3 text-xs text-muted-foreground mb-2">
                      <span>Mood: {insight.metrics?.avgMood}/5</span>
                      <span>Energy: {insight.metrics?.avgEnergy}%</span>
                      <span className={insight.metrics?.consistencyScore === "high" ? "text-green-600 dark:text-green-400" : insight.metrics?.consistencyScore === "low" ? "text-red-600 dark:text-red-400" : "text-yellow-600 dark:text-yellow-400"}>
                        {insight.metrics?.consistencyScore} consistency
                      </span>
                      {insight.metrics?.daysSinceLastCheckIn !== undefined && (
                        <span className={insight.metrics.daysSinceLastCheckIn > 2 ? "text-red-600 dark:text-red-400" : "text-green-600 dark:text-green-400"}>
                          {insight.metrics.daysSinceLastCheckIn}d ago
                        </span>
                      )}
                    </div>

                    {/* Trends */}
                    {insight.trends && (
                      <div className="flex gap-2 mb-2">
                        <div className="flex items-center gap-1 text-xs">
                          {insight.trends.moodTrend === "improving" ? (
                            <TrendingUp className="h-3 w-3 text-green-500" />
                          ) : insight.trends.moodTrend === "declining" ? (
                            <TrendingDown className="h-3 w-3 text-red-500" />
                          ) : null}
                          <span className={insight.trends.moodTrend === "improving" ? "text-green-600 dark:text-green-400" : insight.trends.moodTrend === "declining" ? "text-red-600 dark:text-red-400" : "text-muted-foreground"}>
                            Mood {insight.trends.moodTrend}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-xs">
                          {insight.trends.energyTrend === "increasing" ? (
                            <TrendingUp className="h-3 w-3 text-green-500" />
                          ) : insight.trends.energyTrend === "decreasing" ? (
                            <TrendingDown className="h-3 w-3 text-red-500" />
                          ) : null}
                          <span className={insight.trends.energyTrend === "increasing" ? "text-green-600 dark:text-green-400" : insight.trends.energyTrend === "decreasing" ? "text-red-600 dark:text-red-400" : "text-muted-foreground"}>
                            Energy {insight.trends.energyTrend}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* AI Summary */}
                <div className="mb-3">
                  <p className="text-xs text-foreground leading-relaxed">{insight.summary}</p>
                </div>

                {/* Key Topics */}
                <div className="mb-3">
                  <p className="text-xs font-medium text-foreground mb-1">🎯 Focus Areas:</p>
                  <div className="flex flex-wrap gap-1">
                    {insight.keyTopics.slice(0, 3).map((topic, i) => (
                      <span key={i} className="text-xs bg-blue-100 dark:bg-primary/20 text-blue-700 dark:text-primary px-2 py-1 rounded">
                        {topic}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Data Insights */}
                {insight.dataInsights && insight.dataInsights.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs font-medium text-foreground mb-1">📊 Key Insights:</p>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      {insight.dataInsights.slice(0, 2).map((dataInsight, i) => (
                        <li key={i} className="flex items-start gap-1">
                          <span className="text-blue-500 dark:text-primary mt-0.5">•</span>
                          {dataInsight}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Suggested Questions */}
                <div className="mb-3">
                  <p className="text-xs font-medium text-foreground mb-1">💬 Questions to Ask:</p>
                  <div className="text-xs text-foreground italic">
                    "{insight.suggestedQuestions[0]}"
                    {insight.suggestedQuestions[1] && (
                      <span className="block mt-1">"{insight.suggestedQuestions[1]}"</span>
                    )}
                  </div>
                </div>

                {/* Recommended Actions */}
                {insight.recommendedActions && insight.recommendedActions.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-foreground mb-1">⚡ Next Steps:</p>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      {insight.recommendedActions.slice(0, 2).map((action, i) => (
                        <li key={i} className="flex items-start gap-1">
                          <span className="text-green-500 dark:text-green-400 mt-0.5">→</span>
                          {action}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
            
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full text-blue-700 dark:text-primary border-blue-200 dark:border-border hover:bg-blue-50 dark:hover:bg-muted"
              onClick={() => setShowFullPrep(true)}
            >
              <Calendar className="h-3 w-3 mr-2" />
              View Full 1:1 Preparation
            </Button>
          </div>
        )}
      </CardContent>
    </Card>

    {/* Full 1:1 Preparation Modal */}
    <Dialog open={showFullPrep} onOpenChange={setShowFullPrep}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">🎯 Full 1:1 Preparation Guide</DialogTitle>
          <DialogDescription>
            Comprehensive insights and preparation for your team conversations
          </DialogDescription>
        </DialogHeader>
        
        <div className="overflow-y-auto max-h-[calc(90vh-120px)] pr-2">
          {insights.length === 0 ? (
            <div className="text-center py-8">
              <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No team insights available. Team members need to submit check-ins.</p>
            </div>
          ) : (
            <Tabs defaultValue={insights[0]?.teamMemberId} className="w-full">
              <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${Math.min(insights.length, 4)}, 1fr)` }}>
                {insights.map((insight) => (
                  <TabsTrigger 
                    key={insight.teamMemberId} 
                    value={insight.teamMemberId}
                    className="flex items-center gap-2"
                  >
                    <Avatar className="h-6 w-6">
                      <AvatarImage src={insight.memberAvatar} alt={insight.memberName} />
                      <AvatarFallback className="text-xs">{insight.memberName?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <span className="truncate">{insight.memberName}</span>
                  </TabsTrigger>
                ))}
              </TabsList>

              {insights.map((insight) => (
                <TabsContent key={insight.teamMemberId} value={insight.teamMemberId} className="mt-6 space-y-6">
                  {/* Member Overview */}
                  <div className={`p-6 rounded-lg border ${getPriorityColor(insight.priority)}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-start gap-4">
                        <Avatar className="h-16 w-16">
                          <AvatarImage src={insight.memberAvatar} alt={insight.memberName} />
                          <AvatarFallback className="text-xl">{insight.memberName?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <h3 className="text-xl font-semibold flex items-center gap-2">
                            {insight.memberName}
                            {getMoodIcon(insight.recentMood)}
                          </h3>
                          <div className="flex items-center gap-3 mt-1">
                            <Badge 
                              variant={insight.priority === "high" ? "destructive" : insight.priority === "medium" ? "default" : "secondary"}
                            >
                              {insight.priority} priority
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                              Last check-in: {insight.metrics?.daysSinceLastCheckIn} days ago
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Metrics Dashboard */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="bg-white dark:bg-muted/50 p-3 rounded-lg">
                        <p className="text-xs text-muted-foreground">Avg Mood</p>
                        <p className="text-2xl font-bold">{insight.metrics?.avgMood}/5</p>
                      </div>
                      <div className="bg-white dark:bg-muted/50 p-3 rounded-lg">
                        <p className="text-xs text-muted-foreground">Avg Energy</p>
                        <p className="text-2xl font-bold">{insight.metrics?.avgEnergy}%</p>
                      </div>
                      <div className="bg-white dark:bg-muted/50 p-3 rounded-lg">
                        <p className="text-xs text-muted-foreground">Avg Focus</p>
                        <p className="text-2xl font-bold">{insight.metrics?.avgFocus}%</p>
                      </div>
                      <div className="bg-white dark:bg-muted/50 p-3 rounded-lg">
                        <p className="text-xs text-muted-foreground">Consistency</p>
                        <p className="text-2xl font-bold capitalize">{insight.metrics?.consistencyScore}</p>
                      </div>
                    </div>

                    {/* Trends */}
                    {insight.trends && (
                      <div className="flex gap-4 mb-4">
                        <div className="flex items-center gap-2 bg-white dark:bg-muted/50 px-4 py-2 rounded-lg">
                          {insight.trends.moodTrend === "improving" ? (
                            <TrendingUp className="h-4 w-4 text-green-500" />
                          ) : insight.trends.moodTrend === "declining" ? (
                            <TrendingDown className="h-4 w-4 text-red-500" />
                          ) : (
                            <span className="h-4 w-4 text-gray-400">—</span>
                          )}
                          <span className="text-sm">Mood {insight.trends.moodTrend}</span>
                        </div>
                        <div className="flex items-center gap-2 bg-white dark:bg-muted/50 px-4 py-2 rounded-lg">
                          {insight.trends.energyTrend === "increasing" ? (
                            <TrendingUp className="h-4 w-4 text-green-500" />
                          ) : insight.trends.energyTrend === "decreasing" ? (
                            <TrendingDown className="h-4 w-4 text-red-500" />
                          ) : (
                            <span className="h-4 w-4 text-gray-400">—</span>
                          )}
                          <span className="text-sm">Energy {insight.trends.energyTrend}</span>
                        </div>
                      </div>
                    )}

                    {/* AI Summary */}
                    <div className="bg-white dark:bg-muted/50 p-4 rounded-lg mb-4">
                      <h4 className="font-semibold text-sm mb-2">📝 AI Summary</h4>
                      <p className="text-sm leading-relaxed">{insight.summary}</p>
                    </div>
                  </div>

                  {/* Preparation Details */}
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Key Topics */}
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg">🎯 Key Discussion Topics</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {insight.keyTopics.map((topic, i) => (
                            <div key={i} className="flex items-start gap-3">
                              <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center text-xs font-semibold text-blue-700 dark:text-blue-400">
                                {i + 1}
                              </div>
                              <p className="text-sm">{topic}</p>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Suggested Questions */}
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg">💬 Conversation Starters</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {insight.suggestedQuestions.map((question, i) => (
                            <div key={i} className="p-3 bg-muted/50 rounded-lg">
                              <p className="text-sm italic">"{question}"</p>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Data Insights & Actions */}
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Data Insights */}
                    {insight.dataInsights && insight.dataInsights.length > 0 && (
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg">📊 Data-Driven Insights</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ul className="space-y-2">
                            {insight.dataInsights.map((dataInsight, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm">
                                <span className="text-blue-500 dark:text-blue-400 mt-0.5">•</span>
                                <span>{dataInsight}</span>
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    )}

                    {/* Recommended Actions */}
                    {insight.recommendedActions && insight.recommendedActions.length > 0 && (
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg">⚡ Recommended Actions</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ul className="space-y-2">
                            {insight.recommendedActions.map((action, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm">
                                <span className="text-green-500 dark:text-green-400 mt-0.5">→</span>
                                <span>{action}</span>
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-4">
                    <Button 
                      variant="outline"
                      onClick={handleExport}
                      disabled={isExporting}
                    >
                      {isExporting ? (
                        <>
                          <div className="animate-spin w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full mr-2"></div>
                          Exporting...
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4 mr-2" />
                          Export Notes
                        </>
                      )}
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={handleEmailSend}
                      disabled={isSendingEmail}
                    >
                      {isSendingEmail ? (
                        <>
                          <div className="animate-spin w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full mr-2"></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          <Mail className="h-4 w-4 mr-2" />
                          Email to Self
                        </>
                      )}
                    </Button>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          )}
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default AIOneOnOnePrepWidget;